import React, { useEffect, useState } from 'react';
import { FaEye, FaEyeSlash, FaFax, FaTimes } from 'react-icons/fa';
import { useForm } from 'react-hook-form';
import { InternalFieldName } from 'react-hook-form/dist/types/fields';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/auth';
import api from '../../services/api';
import CustomModal from '../CustomModal/CustomModal';
import {
  EstimateEquipment,
  EstimateViewModel,
} from '../../models/estimate/EstimateViewModel';
import CustomButton from '../CustomButton/CustomButton';
import EstimateStatus from '../../enums/EstimateStatus';
import EquipmentsNew from '../../pages/equipments/EquipmentsNew';

interface EstimateEquipmentsProps {
  estimate: EstimateViewModel;
  items: EstimateEquipment[];
  refreshEquipments: () => void;
  removeEquipment: (estimateEquipment: EstimateEquipment) => void;
}

type EquipmentModel = {
  id: number;
  name: string;
  brand: string;
  model: string;
  serialNumber: string;
};

interface EstimateEquipmentFormFields {
  equipment: number;
}

function addServerErrors<T>(
  errors: { [P in keyof T]?: string[] },
  setError: any
) {
  return Object.keys(errors).forEach((key) => {
    setError(key as keyof T, {
      type: 'server',
      message: errors[key as keyof T]!.join('. '),
    });
  });
}

export default function EstimateEquipments({
  estimate,
  items,
  refreshEquipments,
  removeEquipment,
}: EstimateEquipmentsProps) {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
    reset,
  } = useForm<EstimateEquipmentFormFields>({ mode: 'onBlur' });

  const [isLoading, setIsLoading] = useState(true);
  const [showDescription, setShowDescription] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, any>>([]);
  const [equipments, setEquipments] = useState<EquipmentModel[]>([]);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isOpenEquipmentNewModal, setIsOpenEquipmentNewModal] = useState(false);

  const {
    user: { organization },
  } = useAuth();

  useEffect(() => {
    let shouldFocus = true;
    Object.keys(formErrors || {}).forEach((field: InternalFieldName) => {
      addServerErrors(formErrors, setError);

      shouldFocus = false;
    });
  }, [formErrors]);

  async function getEquipments() {
    if (organization) {
      setIsLoading(true);
      setIsOpenModal(true);
      const response = await api.get(
        `organizations/${organization.id}/estimates/${estimate.id}/equipments/available`
      );
      setEquipments(response.data.data);
      setIsLoading(false);
    }
  }

  const onSubmit = async (data: EstimateEquipmentFormFields) => {
    try {
      await api.post(
        `organizations/${organization.id}/estimates/${estimate.id}/equipments`,
        data
      );

      setEquipments([]);
      setIsOpenModal(false);
      refreshEquipments();
    } catch (e: any) {
      if (e?.response?.data?.errors) {
        if (e?.response?.status === 422) {
          setFormErrors(e.response.data.errors);
        } else {
          toast.error(
            e?.response?.data?.errors.reduce(
              (acc: string, error: string) => acc + error,
              ''
            )
          );
        }
      } else {
        throw e;
      }
    }
  };

  return (
    <>
      <div className='d-flex justify-content-between gap-1 align-items-center'>
        <span className='h2'>Equipamentos</span>
        <button
          type='button'
          className='btn btn-secondary btn-sm'
          onClick={() => setIsOpenEquipmentNewModal(true)}
        >
          <span>Cadastrar equipamento</span> <FaFax />
        </button>
      </div>
      <div className='table-responsive'>
        <table className='table'>
          <thead>
            <tr>
              <th>Nome</th>
              <th>Marca</th>
              <th>Modelo</th>
              <th>N/S</th>
              <th>Observações</th>
              <th style={{ width: '20px' }}>&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {items.length > 0 ? (
              items.map((item) => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td>{item.brand}</td>
                  <td>{item.model}</td>
                  <td>{item.serialNumber}</td>
                  <td>
                    <div className='d-flex align-content-center gap-2'>
                      {item.description ? (
                        <>
                          <div
                            className={!showDescription ? `text-truncate` : ''}
                            style={{ width: '20rem' }}
                          >
                            {item.description}
                          </div>
                          <div>
                            <span
                              onClick={() =>
                                setShowDescription(!showDescription)
                              }
                              role='button'
                              aria-hidden='true'
                            >
                              {showDescription && <FaEyeSlash />}
                              {!showDescription && <FaEye />}
                            </span>
                          </div>
                        </>
                      ) : (
                        <i>Sem observação</i>
                      )}
                    </div>
                  </td>
                  <td>
                    {estimate.status !== EstimateStatus.Completed &&
                      estimate.status !== EstimateStatus.Canceled && (
                        <button
                          className='btn btn-danger btn-sm'
                          type='button'
                          onClick={() => removeEquipment(item)}
                        >
                          <FaTimes />
                        </button>
                      )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5}>Nenhum registro</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className='text-end'>
        {estimate.status !== EstimateStatus.Completed &&
          estimate.status !== EstimateStatus.Canceled && (
            <button
              type='button'
              className='btn btn-secondary btn-sm'
              onClick={() => getEquipments()}
            >
              Adicionar equipamento <FaFax />
            </button>
          )}

        <CustomModal
          isOpen={isOpenEquipmentNewModal}
          modalTitle='Novo Equipamento'
          setIsOpenModal={setIsOpenEquipmentNewModal}
        >
          <EquipmentsNew
            customerId={estimate.customerId}
            setIsOpenModal={setIsOpenEquipmentNewModal}
          />
        </CustomModal>

        <CustomModal
          modalTitle='Adicionar equipamento'
          isOpen={isOpenModal}
          setIsOpenModal={setIsOpenModal}
          fullscreenOnMobile={false}
        >
          <form
            style={{
              paddingTop: '1rem',
              paddingBottom: '1rem',
            }}
          >
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className='form-label'>Selecione o equipamento</label>
            <select
              required
              className='form-select'
              aria-label='Selecione o equipamento'
              {...register('equipment', { required: true })}
            >
              <option value=''>Selecione o equipamento</option>
              {equipments.map((equipment) => (
                <option value={equipment.id} key={equipment.id}>
                  {equipment.name} - {equipment.model} (N/S:{' '}
                  {equipment.serialNumber})
                </option>
              ))}
            </select>
            {errors.equipment && (
              <div className='form-text text-danger'>
                {errors.equipment.message}
              </div>
            )}
            <div className='text-end mt-3'>
              <CustomButton
                type='button'
                className='btn btn-primary'
                handleClick={handleSubmit(onSubmit)}
              >
                Adicionar
              </CustomButton>
            </div>
          </form>
        </CustomModal>
      </div>
    </>
  );
}
